import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { money } from "../lib/format";

/**
 * Depop tie-out. Upload the seller CSV, see every report row lined up against
 * what the app has, then apply only the changes you approve. Read-only until the
 * Apply button is pressed.
 */

type MatchBasis = "order-ref" | "sku" | "inferred" | "none";

interface FieldChange {
  field: "platform" | "salePrice" | "soldAt" | "externalOrderId" | "buyerState" | "boosted";
  from: string | number | boolean | null;
  to: string | number | boolean | null;
  reason: string;
}

interface Row {
  line: number;
  orderRef: string;
  buyer: string;
  soldDate: string;
  bundle: boolean;
  bundleSize: number;
  reportBrand: string | null;
  reportDescription: string | null;
  reportSize: string | null;
  reportPrice: number;
  reportBoosted: boolean;
  orderItemTotal: number;
  orderShipping: number;
  orderTax: number;
  orderFee: number;
  orderBoostFee: number;
  orderPayout: number;
  basis: MatchBasis;
  confidence: number;
  saleId: string | null;
  itemId: string | null;
  sku: string | null;
  appTitle: string | null;
  appPlatform: string | null;
  appPrice: number | null;
  appSoldAt: string | null;
  appOrderRef: string | null;
  status: "tied" | "needs-change" | "unmatched";
  changes: FieldChange[];
  notes: string[];
}

interface Extra {
  saleId: string;
  itemId: string;
  sku: string;
  title: string | null;
  salePrice: number | null;
  soldAt: string | null;
  externalOrderId: string | null;
  note: string;
}

interface Summary {
  reportOrders: number;
  reportItems: number;
  reportItemTotal: number;
  reportShipping: number;
  reportTax: number;
  reportFees: number;
  reportBoostFees: number;
  boostedItems: number;
  reportPayout: number;
  refundedOrders: number;
  reportRefunded: number;
  appDepopSales: number;
  appDepopTotal: number;
  tied: number;
  needsChange: number;
  unmatched: number;
  extras: number;
  skuColumnPopulated: number;
}

interface Result { rows: Row[]; extras: Extra[]; summary: Summary }

const FIELD_LABEL: Record<FieldChange["field"], string> = {
  platform: "Channel",
  salePrice: "Sale price",
  soldAt: "Sold on",
  externalOrderId: "Order reference",
  buyerState: "Buyer state",
  boosted: "Boosted",
};

function showValue(field: FieldChange["field"], v: string | number | boolean | null): string {
  if (field === "boosted") return v ? "yes" : "no";
  if (v === null || v === "") return "not set";
  if (field === "salePrice") return money(v as number);
  if (field === "soldAt") return String(v).slice(0, 10);
  return String(v);
}

function basisLabel(b: MatchBasis): string {
  if (b === "order-ref") return "order ref";
  if (b === "sku") return "SKU";
  if (b === "inferred") return "inferred";
  return "no match";
}

export function Reconcile() {
  const [result, setResult] = useState<Result | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<number | null>(null);
  // Rows the user has unticked; everything actionable starts ticked.
  const [skipped, setSkipped] = useState<Set<number>>(new Set());

  const actionable = useMemo(
    () => (result?.rows ?? []).filter((r) => r.status === "needs-change" && (r.saleId || r.itemId)),
    [result]
  );
  const selected = useMemo(
    () => actionable.filter((r) => !skipped.has(r.line)),
    [actionable, skipped]
  );

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    setResult(null);
    setApplied(null);
    setSkipped(new Set());
    setFileName(file.name);
    try {
      const csv = await file.text();
      setResult(await api.post<Result>("/reconcile/depop", { csv }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read that report");
    } finally {
      setBusy(false);
    }
  }

  async function applyChanges() {
    if (selected.length === 0) return;
    const n = selected.length;
    if (!window.confirm(`Apply ${n} change${n === 1 ? "" : "s"} to your sales records?`)) return;

    setBusy(true);
    setError(null);
    try {
      const operations = selected.map((r) => {
        const fields: Record<string, unknown> = {};
        for (const c of r.changes) fields[c.field] = c.to;
        return r.saleId
          ? { kind: "updateSale" as const, saleId: r.saleId, fields }
          : {
              kind: "createSale" as const,
              itemId: r.itemId!,
              fields: {
                salePrice: r.reportPrice,
                soldAt: fields.soldAt ?? `${r.soldDate}T12:00:00.000Z`,
                externalOrderId: fields.externalOrderId,
                buyerState: fields.buyerState ?? null,
                boosted: r.reportBoosted,
              },
            };
      });
      const res = await api.post<{ count: number }>("/reconcile/depop/apply", { operations });
      setApplied(res.count);
      setResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not apply those changes");
    } finally {
      setBusy(false);
    }
  }

  const s = result?.summary;

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">tie out</span>
        {s && <span className="num" style={{ color: "var(--muted)" }}>{s.reportItems}</span>}
      </header>

      {error && <div className="notice">{error}</div>}

      {applied !== null && (
        <div className="notice" style={{ background: "var(--pos-bg, #eef7ee)" }}>
          Applied {applied} change{applied === 1 ? "" : "s"}. Upload the report again to confirm
          everything now ties, or head to <Link to="/sales">Sold</Link>.
        </div>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">Depop sales report</h2>
        <p className="hint">
          Export from Depop, then drop the CSV here. Nothing is written until you press Apply.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
        />
        {fileName && <p className="hint">{fileName}{busy ? " — matching…" : ""}</p>}
      </section>

      {s && (
        <>
          <section className="ledger">
            <div>
              <span className="eyebrow">Report items</span>
              <span className="value">{s.reportItems}</span>
            </div>
            <div>
              <span className="eyebrow">Orders</span>
              <span className="value">{s.reportOrders}</span>
            </div>
            <div>
              <span className="eyebrow">Item revenue</span>
              <span className="value">{money(s.reportItemTotal)}</span>
            </div>
            <div>
              <span className="eyebrow">Depop fees</span>
              <span className="value neg">{money(s.reportFees)}</span>
            </div>
            {s.reportBoostFees > 0 && (
              <div>
                <span className="eyebrow">Boosting ({s.boostedItems})</span>
                <span className="value neg">{money(s.reportBoostFees)}</span>
              </div>
            )}
            <div>
              <span className="eyebrow">Net payout</span>
              <span className="value pos">{money(s.reportPayout)}</span>
            </div>
            {s.refundedOrders > 0 && (
              <div>
                <span className="eyebrow">Refunded ({s.refundedOrders})</span>
                <span className="value neg">{money(s.reportRefunded)}</span>
              </div>
            )}
          </section>

          <section className="rail">
            <h2 className="eyebrow rail-label">Where you stand</h2>
            <dl className="record">
              <div className="record-row">
                <dt>Already tied</dt><dd className="num">{s.tied}</dd>
              </div>
              <div className="record-row">
                <dt>Need a change</dt><dd className="num">{s.needsChange}</dd>
              </div>
              <div className="record-row">
                <dt>No match found</dt><dd className="num">{s.unmatched}</dd>
              </div>
              <div className="record-row">
                <dt>Depop sales in app, not in report</dt><dd className="num">{s.extras}</dd>
              </div>
              <div className="record-row">
                <dt>App revenue recorded</dt>
                <dd className="num">
                  {money(s.appDepopTotal)}
                  {Math.abs(s.appDepopTotal - s.reportItemTotal) > 0.005 && (
                    <span className="neg"> ({money(s.appDepopTotal - s.reportItemTotal)} vs report)</span>
                  )}
                </dd>
              </div>
            </dl>
            {s.skuColumnPopulated === 0 && (
              <p className="hint">
                The report's SKU column is empty on every row. Put the app SKU into each Depop
                listing's SKU field and future reports will tie out exactly, with no guessing.
              </p>
            )}
            <p className="hint">
              Buyer shipping ({money(s.reportShipping)}) and sales tax ({money(s.reportTax)}) are
              buyer-funded and stay out of your revenue — Depop buys the label and remits the tax.
            </p>
          </section>

          <section className="rail">
            <h2 className="eyebrow rail-label">
              Report rows ({result!.rows.length}) — {selected.length} selected to apply
            </h2>

            {result!.rows.map((r) => {
              const off = skipped.has(r.line);
              const canApply = r.status === "needs-change" && (r.saleId || r.itemId);
              return (
                <div key={r.line} className="sale-row" style={{ opacity: off && canApply ? 0.55 : 1 }}>
                  <div className="tag" style={{ cursor: "default", alignItems: "flex-start" }}>
                    {canApply && (
                      <input
                        type="checkbox"
                        checked={!off}
                        style={{ marginTop: "0.4rem" }}
                        onChange={() =>
                          setSkipped((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.line)) next.delete(r.line);
                            else next.add(r.line);
                            return next;
                          })
                        }
                      />
                    )}
                    <span className="tag-body">
                      <span className="tag-title">
                        {r.reportBrand ?? "Unknown brand"}
                        {r.reportSize ? ` · ${r.reportSize}` : ""}
                        {r.bundle && (
                          <span className="tag-meta"> · bundle of {r.bundleSize}</span>
                        )}
                      </span>
                      <span className="tag-meta">
                        <span>CSV line {r.line}</span>
                        <span>{r.soldDate}</span>
                        <span>{r.buyer}</span>
                        <span>
                          {r.status === "tied"
                            ? "ties"
                            : r.status === "unmatched"
                              ? "no match"
                              : `${basisLabel(r.basis)} · ${Math.round(r.confidence * 100)}%`}
                        </span>
                      </span>
                      <span className="hint" style={{ margin: "0.25rem 0 0" }}>
                        {r.reportDescription}
                      </span>
                      {r.sku && (
                        <span className="tag-meta">
                          →{" "}
                          <Link to={`/items/${r.itemId}`}>{r.sku}</Link>{" "}
                          {r.appTitle} · {r.appPlatform ?? "no sale"}{" "}
                          {r.appPrice !== null ? money(r.appPrice) : ""}
                        </span>
                      )}
                    </span>
                    <span className="tag-price">
                      <span className="ask">{money(r.reportPrice)}</span>
                      <span className="tag-meta">
                        order {money(r.orderItemTotal)} − fee {money(r.orderFee)}
                        {r.orderBoostFee > 0 && <> − boost {money(r.orderBoostFee)}</>}
                      </span>
                    </span>
                  </div>

                  {r.changes.length > 0 && (
                    <div className="tag-indent">
                      {r.changes.map((c) => (
                        <div key={c.field} className="tag-meta" style={{ display: "block" }}>
                          <strong>{FIELD_LABEL[c.field]}</strong>{" "}
                          <span className="neg">{showValue(c.field, c.from)}</span> →{" "}
                          <span className="pos">{showValue(c.field, c.to)}</span>
                          <span className="hint" style={{ margin: 0 }}> {c.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {r.notes.length > 0 && (
                    <div className="tag-indent">
                      {r.notes.map((n, k) => (
                        <p key={k} className="hint" style={{ margin: "0.15rem 0" }}>⚠ {n}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="actions">
              <button onClick={() => void applyChanges()} disabled={busy || selected.length === 0}>
                {busy ? "Applying" : `Apply ${selected.length} change${selected.length === 1 ? "" : "s"}`}
              </button>
              <button
                className="quiet"
                disabled={busy || actionable.length === 0}
                onClick={() =>
                  setSkipped((prev) =>
                    prev.size === actionable.length ? new Set() : new Set(actionable.map((r) => r.line))
                  )
                }
              >
                {skipped.size === actionable.length && actionable.length > 0 ? "Select all" : "Clear all"}
              </button>
            </div>
          </section>

          {result!.extras.length > 0 && (
            <section className="rail">
              <h2 className="eyebrow rail-label">
                Tagged Depop but not in this report ({result!.extras.length})
              </h2>
              <p className="hint">
                These are left alone — deciding what they really are needs your judgement. Fix them
                from <Link to="/sales">Sold</Link>.
              </p>
              {result!.extras.map((e) => (
                <div key={e.saleId} className="tag" style={{ cursor: "default" }}>
                  <span className="tag-body">
                    <span className="tag-title">
                      <Link to={`/items/${e.itemId}`}>{e.title ?? e.sku}</Link>
                    </span>
                    <span className="tag-meta">
                      <span>{e.sku}</span>
                      <span>{e.soldAt ? String(e.soldAt).slice(0, 10) : "—"}</span>
                    </span>
                    <span className="hint" style={{ margin: "0.25rem 0 0" }}>{e.note}</span>
                  </span>
                  <span className="tag-price">
                    <span className="ask">{money(e.salePrice)}</span>
                  </span>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
